<template>
  <screen
    class="fixed bg-white top-0 left-0 justify-start"
    :screen-info="message"
    :can-skip="false"
    :can-leave="canLeave"
    @leave="$emit('cancel')"
  >
    <circle-progress class="loading-circle" :steps="steps" :step="step" @ended="$emit('done')" />
    <qrcode-stream
      v-if="showCamera && !showPrompt && !invalid"
      class="qr-stream-wrapper"
      :camera="qrCameraType"
      :formats="['qr_code', 'code_128']"
      @init="onInit"
      @decode="onDecode"
    >
      <switch-camera-button class="camera-switch-btn" @click="handleSwitchCamera" />
      <div v-if="canShareMyCode" class="open-qr-link" @click="showMyQRCode">
        <img class="show-rq-icon" src="../../assets/common/show-qr-code.svg" />
        <span>{{ $t('welcome.share_qr_code') }}</span>
      </div>
    </qrcode-stream>
    <p v-if="errorHint" class="qr-stream-wrapper my-10 text-red-600 text-center">
      {{ errorHint }}
    </p>
    <!-- Prompt -->
    <rounded-card
      :height="`70%`"
      v-if="showPrompt && accessLongToken"
      show-right-btn
      class="p-3"
      @set-close-status="cancel"
    >
      <div class="flex text-center w-full flex-col px-4 py-4">
        <template>
          <h3 class="text-xl">
            {{ $t('welcome.my_qr_code') }}
          </h3>
          <div v-if="accessLongToken" class="qr-code-wrap">
            <vue-qr class="my-4" v-bind="dafQrSettings" />
          </div>
          <div class="reminder-text">
            {{ $t('welcome.scan_qr_code_reminder') }}
          </div>
        </template>
      </div>
    </rounded-card>
    <rounded-card v-if="invalid" height="auto" no-head class="qr-invalid-card">
      <div class="flex flex-col items-center text-center px-6 py-8 w-full">
        <h3 class="text-xl font-bold mb-16">{{ $t('mobileScan.qr_not_valid') }}</h3>
        <p class="mb-16">{{ $t('mobileScan.qr_not_valid_desc') }}</p>
        <c-button class="mt-auto back-home-button" @click="onBackToHome">
          <span>{{ $t('mobileScan.back_to_home') }}</span>
        </c-button>
      </div>
    </rounded-card>
  </screen>
</template>

<script>
import { mapGetters } from 'vuex';
import VueQr from 'vue-qr';
import { QrcodeStream } from 'vue-qrcode-reader';
import { EVENTS as HW_EVENTS } from '@/events/hardware';
import { wait } from '@/modules/utils';
import Screen from '@/components/common/Screen.vue';
import CircleProgress from '@/components/Animations/CircleProgress.vue';
import RoundedCard from '@/components/common/RoundedCard.vue';
import SwitchCameraButton from '@/components/common/SwitchCameraButton.vue';
import CButton from '@/components/common/CButton.vue';

export default {
  name: 'QrScan',
  components: {
    Screen,
    CircleProgress,
    QrcodeStream,
    RoundedCard,
    VueQr,
    SwitchCameraButton,
    CButton,
  },
  props: {
    showCamera: Boolean,
    useFrontCamera: Boolean,
    step: Number,
    steps: Number,
    myCode: Boolean,
    // emit the decoded text to the parent instead of the global event bus, so
    // that page level scans don't also trigger the app wide QRCODE_SUCCESS
    // handler in the handle-qr-scan mixin
    localOnly: Boolean,
    canLeave: {
      type: Boolean,
      default: true,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    message: {
      type: String,
      default: 'Scan QR Code to begin',
    },
  },
  data: () => ({
    errorHint: '',
    showPrompt: false,
    currentCamera: false,
    isCameraOff: false,
  }),
  computed: {
    ...mapGetters('user', ['accessLongToken']),
    ...mapGetters('user', ['rmdSizes', 'tempAccess']),
    ...mapGetters('mtmUser', ['isContactless']),
    // Only for Esquel溢達 to recommend size, cannot scan to login in the cloudzet.
    dafQrSettings() {
      return {
        text: `${window.location.origin}/?token=${this.accessLongToken}&scan=false`,
        size: 350,
        margin: 10,
      };
    },
    hasRmdSizes() {
      return Object.keys(this.rmdSizes).length > 0;
    },
    canShareMyCode() {
      // Temporarily disabled: hides the "share my code" entry point.
      return false && !this.tempAccess && this.myCode && this.accessLongToken;
    },
    qrCameraType() {
      if (this.isCameraOff) return 'off';
      return this.currentCamera ? 'front' : 'auto';
    },
  },
  methods: {
    showMyQRCode() {
      this.showPrompt = true;
    },
    cancel() {
      this.showPrompt = false;
    },
    close() {
      this.showPrompt = false;
    },
    onBackToHome() {
      this.$emit('dismiss-invalid');
      this.$emit('cancel');
      // contactless kiosks live on Welcome, everyone else on HomePage
      this.$events.goto(this.isContactless ? 'Welcome' : 'HomePage');
    },
    onDecode(text) {
      if (this.localOnly) {
        this.$emit('decode', text);
        return;
      }
      this.$events.emit(HW_EVENTS.QRCODE_SUCCESS, text);
      // this.$emit('cancel');
    },
    async onInit(promise) {
      console.log('[QRSCAN] qr camera init with camera type:', this.qrCameraType);
      this.errorHint = '';
      try {
        await promise;
        // successfully initialized
        const video = document.querySelector('.qrcode-stream-wrapper video');
        if (video && video.paused) {
          video.play();
        }
      } catch (error) {
        this.errorHint = this.$t('welcome.need_camera_permission');
        if (error.name === 'NotAllowedError') {
          return;
        }
        console.log(`[ERROR] qr scan camera error: ${error.message} (${error.name})`);
        // NotAllowedError: user denied camera access permission
        // NotFoundError: no suitable camera device installed
        // NotSupportedError: page is not served over HTTPS (or localhost)
        // NotReadableError: maybe camera is already in use
        // OverconstrainedError: did you requested the front camera although there is none?
        // StreamApiNotSupportedError: browser seems to be lacking features
      }
    },
    async restartCamera() {
      console.log('[QRSCAN] restarting camera...');
      this.isCameraOff = true;
      await wait(0.5);
      this.isCameraOff = false;
    },
    handleSwitchCamera() {
      this.currentCamera = !this.currentCamera;
      if (this.useFrontCamera) {
        this.setQRDefaultCamera();
      }
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.restartCamera();
      }
    },
    getQRDefaultCamera() {
      this.currentCamera = localStorage.getItem('qrCamera') === 'front';
    },
    setQRDefaultCamera() {
      localStorage.setItem('qrCamera', this.currentCamera ? 'front' : 'auto');
    },
  },
  created() {
    this.currentCamera = this.useFrontCamera;
    if (this.useFrontCamera) {
      this.getQRDefaultCamera();
    }
  },
  watch: {
    showCamera(newVal) {
      if (newVal) {
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
      } else {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.isCameraOff = false;
      }
    },
    useFrontCamera(newVal) {
      this.currentCamera = newVal;
    },
  },
};
</script>

<style lang="scss" scoped>
.loading-circle {
  @apply fixed left-0 right-0 mx-auto;
  top: 50%;
  transform: translateY(-50%);
  width: 80%;
  max-width: 700px;
}
.qr-stream-wrapper {
  @extend .loading-circle;
  max-width: 364px;
  max-height: 366px;
  top: calc(50% + 50px);
}

.qr-code-wrap {
  width: 200px;
  height: 200px;
  margin: auto;
  margin-bottom: 2rem;
}

::v-deep .qrcode-stream-camera {
  // NOTE: match the design of the one in native app
  border-radius: 19px;

  border: 4px solid transparent;
  background-image: linear-gradient(white, white),
    linear-gradient(90deg, #8146ff -2.22%, #4659ff 103.19%);
  background-origin: border-box;
  background-clip: content-box, border-box;

  --s: 60px; /* the size on the corner */
  mask: conic-gradient(at var(--s) var(--s), #0000 75%, #000 0) 0 0 / calc(100% - var(--s))
      calc(100% - var(--s)),
    linear-gradient(#000 0 0) content-box;
}

.open-qr-link {
  @extend .loading-circle;
  cursor: pointer;
  top: auto;
  bottom: -70px;
  display: flex;
  justify-content: center;
  align-items: center;

  span {
    background: linear-gradient(to right, #2245ff 0%, #540deb 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .show-rq-icon {
    margin-right: 0.5rem;
  }
}

.camera-switch-btn {
  position: absolute;
  z-index: 10;
  top: -60px;
  left: 50%;
  transform: translateX(-50%);
}

.qr-invalid-card ::v-deep .c-button {
  max-width: none;
}

.back-home-button {
  margin-bottom: calc(2rem + env(safe-area-inset-bottom));
}

@media screen and (min-width: 576px) {
  .qr-code-wrap {
    width: 350px;
    height: 350px;

    img {
      width: 100%;
    }
  }
}
</style>
